import { StateCreator } from 'zustand';
import { MESSAGE_IDS } from '@trust_network/shared/message-ids';
import {
  fetchDelegationState,
  requestDelegation,
  requestDelegationRevoke,
} from '../api/delegation-api';
import { NotificationSlice } from '../../../features/notifications/model/types';
import { translateMessageId } from '../../../shared/i18n';
import {
  getApiActionErrorResult,
  normalizeApiError,
} from '../../../shared/api/lib/error';
import { LoadingState } from '../../../shared/store/model/types';
import { UserSlice } from '../../user/model/types';
import { DelegateFieldErrors, DelegationSlice } from './types';

type DelegationStoreState = DelegationSlice &
  NotificationSlice &
  Pick<UserSlice, 'token' | 'user'> & {
    loading: LoadingState;
  };

export const createDelegationSlice: StateCreator<
  DelegationStoreState,
  [],
  [],
  DelegationSlice
> = (set, get) => ({
  chain: [],
  root: null,
  delegate: async (toUserId) => {
    const token = get().token;
    if (!token) {
      const message = translateMessageId(MESSAGE_IDS.requestFailed);
      get().notify(message);
      return {
        success: false,
        formError: message,
      };
    }

    if (!toUserId.trim()) {
      return {
        success: false,
        fieldErrors: {
          toUserId: translateMessageId(
            MESSAGE_IDS.validationDelegateTargetRequired
          ),
        },
      };
    }

    if (get().user?.id && toUserId.trim() === get().user?.id) {
      return {
        success: false,
        fieldErrors: {
          toUserId: translateMessageId(MESSAGE_IDS.selfDelegationForbidden),
        },
      };
    }

    set((state) => ({
      loading: { ...state.loading, delegate: true },
    }));

    try {
      await requestDelegation(token, toUserId);

      const delegation = await fetchDelegationState(token);
      set((state) => ({
        chain: delegation.chain,
        root: delegation.root,
        loading: { ...state.loading, delegate: false },
      }));
      get().notify('Голос успешно передан.', 'success');
      return { success: true };
    } catch (error) {
      set((state) => ({
        loading: { ...state.loading, delegate: false },
      }));
      return getApiActionErrorResult<DelegateFieldErrors>(
        error,
        translateMessageId,
        {
          [MESSAGE_IDS.approvalRequired]: 'toUserId',
          [MESSAGE_IDS.cycleDetected]: 'toUserId',
          [MESSAGE_IDS.selfDelegationForbidden]: 'toUserId',
          [MESSAGE_IDS.userNotFound]: 'toUserId',
        }
      );
    }
  },
  revoke: async () => {
    const token = get().token;
    if (!token) {
      const message = translateMessageId(MESSAGE_IDS.requestFailed);
      get().notify(message);
      return {
        success: false,
        formError: message,
      };
    }

    set((state) => ({
      loading: { ...state.loading, delegate: true },
    }));

    try {
      await requestDelegationRevoke(token);

      const delegation = await fetchDelegationState(token);
      set((state) => ({
        chain: delegation.chain,
        root: delegation.root,
        loading: { ...state.loading, delegate: false },
      }));
      get().notify('Передача голоса отменена.', 'success');
      return { success: true };
    } catch (error) {
      set((state) => ({
        loading: { ...state.loading, delegate: false },
      }));
      const message = normalizeApiError(error, translateMessageId);
      get().notify(message);
      return {
        success: false,
        formError: message,
      };
    }
  },
  refreshDelegation: async () => {
    const token = get().token;
    if (!token) {
      return;
    }

    const delegation = await fetchDelegationState(token);
    set({
      chain: delegation.chain,
      root: delegation.root,
    });
  },
});
